/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 05/08/2025 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Arrastre y tecla rápida por menú
*      3       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Se estabiliza asa de arrastre de menús
*      4       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Bloqueo de edición en árbol de menús
*      5       | 26/08/2026 | Lic. José Luis Aragón Cervantes  | Arrastre de menús padres (grupos)
* ---------------------------------------------------------------------------------------
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/CustomTreeView.css";
import MenuIcon from "@mui/icons-material/Menu";

const getInitialCheckedLeaves = (nodes) => {
  const checked = [];
  const collect = (arr) => {
    arr.forEach((n) => {
      const hasChildren = Array.isArray(n.children) && n.children.length > 0;
      if (!hasChildren) {
        if (n.mostrar && n.id2) checked.push(n.id2);
      } else {
        collect(n.children);
      }
    });
  };
  collect(nodes);
  return checked;
};

const SizeSoftXCheckbox = ({ checked, indeterminate, onChange, dataId, disabled = false }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={!!checked}
      data-id={dataId}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      onChange={onChange}
    />
  );
};

const TreeNode = ({
  node,
  withCheckboxes,
  onCheck,
  getNodeChecked,
  getNodeIndeterminate,
  onHotKeyChange,
  onReorder,
  onReorderParents,
  icons = MenuIcon,
  isRoot = false,
  parentId = "",
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const IconComponent = node.icon || icons.default;

  const checked = getNodeChecked(node);
  const indeterminate = getNodeIndeterminate(node);

  const handleExpand = (e) => {
    e.stopPropagation();
    setExpanded((p) => !p);
  };

  const handleClickNode = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onCheck(node.id2, !checked, node);
  };

  const handleDragStart = (e) => {
    if (disabled) return;
    e.stopPropagation();
    const payload = JSON.stringify({
      parentId,
      nodeId: node.id2,
      nodeType: hasChildren ? "parent" : "child",
    });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-sizesoftx-menu", payload);
    e.dataTransfer.setData("text/plain", payload);
  };

  const handleDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();

    try {
      const rawPayload =
        e.dataTransfer.getData("application/x-sizesoftx-menu") ||
        e.dataTransfer.getData("text/plain");
      const payload = JSON.parse(rawPayload);

      if (payload.nodeType === "parent" && hasChildren) {
        onReorderParents?.(payload.nodeId, node.id2);
      } else if (payload.nodeType === "child" && !hasChildren) {
        if (payload.parentId !== parentId) return;
        onReorder?.(parentId, payload.nodeId, node.id2);
      }
    } catch {
      return;
    }
  };

  return (
    <div className={`tree-node ${isRoot ? "root-node" : ""}`}>
      <div
        className={`tree-node-label ${disabled ? "is-disabled" : ""}`}
        onClick={handleClickNode}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {hasChildren && (
          <button
            className="expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleExpand(e);
            }}
          >
            {expanded ? "-" : "+"}
          </button>
        )}

        {withCheckboxes && (
          <SizeSoftXCheckbox
            checked={checked}
            indeterminate={indeterminate}
            dataId={node.id2}
            disabled={disabled}
            onChange={(e) => onCheck(node.id2, e.target.checked, node)}
          />
        )}

        {IconComponent && (
          <span className="node-icon">{<IconComponent />}</span>
        )}
        <span
          className="drag-handle"
          title={hasChildren ? "Arrastrar para reordenar grupo" : "Arrastrar para ordenar"}
          draggable={!disabled}
          onClick={(e) => e.stopPropagation()}
          onDragStart={handleDragStart}
        >
          ||
        </span>
        <span style={{ cursor: disabled ? "default" : "pointer" }}>{node.label}</span>
        {!hasChildren && (
          <span
            className="node-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <label>
              Tecla
              <input
                type="text"
                maxLength={1}
                value={node.hotkey ?? ""}
                disabled={disabled}
                onChange={(e) => onHotKeyChange?.(node.id2, e.target.value)}
              />
            </label>
          </span>
        )}
      </div>

      {expanded && hasChildren && (
        <div className="tree-children">
          {node.children.map((child) => (
            <TreeNode
              key={child.id2}
              node={child}
              withCheckboxes={withCheckboxes}
              onCheck={onCheck}
              getNodeChecked={getNodeChecked}
              getNodeIndeterminate={getNodeIndeterminate}
              onHotKeyChange={onHotKeyChange}
              onReorder={onReorder}
              onReorderParents={onReorderParents}
              icons={icons}
              parentId={node.id2}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function SizeSoftXRouteView({
  data,
  withCheckboxes = false,
  onChange,
  icons = {},
  changeDataStatus,
  onHotKeyChange,
  onReorder,
  onReorderParents,
  disabled = false,
}) {
  const [checkedLeaves, setCheckedLeaves] = useState([]);

  const findNodeById2 = (id2, nodes) => {
    for (let n of nodes) {
      if (n.id2 === id2) return n;
      if (n.children) {
        const found = findNodeById2(id2, n.children);
        if (found) return found;
      }
    }
    return null;
  };

  const collectLeafIds = (node) => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    if (!hasChildren) return [node.id2];
    return node.children.flatMap(collectLeafIds);
  };

  const getNodeChecked = useMemo(() => {
    const isChecked = (node) => {
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      if (!hasChildren) return checkedLeaves.includes(node.id2);
      return node.children.every(isChecked);
    };
    return isChecked;
  }, [checkedLeaves]);

  const getNodeIndeterminate = useMemo(() => {
    const isInd = (node) => {
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      if (!hasChildren) return false;

      const anyOn = node.children.some((c) => getNodeChecked(c) || isInd(c));
      const allOn = node.children.every((c) => getNodeChecked(c));
      return anyOn && !allOn;
    };
    return isInd;
  }, [getNodeChecked]);

  const toggleCheck = (nodeId2, nextChecked, nodeRef) => {
    if (disabled) return;
    const node = nodeRef || findNodeById2(nodeId2, data);
    if (!node) return;

    const leafIds = collectLeafIds(node);
    let next = [...checkedLeaves];

    if (nextChecked) {
      next = [...new Set([...next, ...leafIds])];
    } else {
      next = next.filter((id) => !leafIds.includes(id));
    }

    setCheckedLeaves(next);

    if (changeDataStatus) changeDataStatus(nodeId2, nextChecked, node);

    if (onChange) {
      const checkedLeafObjects = next
        .map((id) => findNodeById2(id, data))
        .filter(Boolean);
      onChange(checkedLeafObjects);
    }
  };

  useEffect(() => {
    if (data?.length > 0) setCheckedLeaves(getInitialCheckedLeaves(data));
  }, [data]);

  return (
    <div className="custom-tree">
      {data.map((node) => (
        <TreeNode
          key={node.id2}
          node={node}
          withCheckboxes={withCheckboxes}
          onCheck={toggleCheck}
          getNodeChecked={getNodeChecked}
          getNodeIndeterminate={getNodeIndeterminate}
          onHotKeyChange={onHotKeyChange}
          onReorder={onReorder}
          onReorderParents={onReorderParents}
          icons={{ default: icons.default }}
          isRoot={true}
          parentId={node.id2}
          disabled={disabled}
        />
      ))}
    </div>
  );
}
